import { VacancyTextVariant } from "../types/campaignPrefill";

type TaxonomyItem = { vonqId?: number; description?: string };
type FormPair = { id: number; name: string } | null;

function toFormPair(item?: TaxonomyItem | null): FormPair {
  if (!item || item.vonqId == null || !item.description) return null;
  return { id: Number(item.vonqId), name: String(item.description) };
}

function targetGroupPair(item?: TaxonomyItem | null): FormPair {
  return toFormPair(item);
}

export function buildWizardDataFromCampaignRecord(campaign: Record<string, any>) {
  const snapshot = (campaign.vonqMappingSnapshot || {}) as Record<string, any>;
  const pd = (campaign.postingDetails || {}) as Record<string, any>;
  const wl = (pd.workingLocation || {}) as Record<string, any>;
  const tg = (campaign.targetGroup || {}) as Record<string, any>;
  const salary = (pd.salaryIndication || {}) as Record<string, any>;
  const salaryRange = (salary.range || {}) as Record<string, any>;
  const weekly = (pd.weeklyWorkingHours || {}) as Record<string, any>;
  const contact = (pd.contactInfo || {}) as Record<string, any>;
  const recruiter = (campaign.recruiterInfo || {}) as Record<string, any>;

  const edu = tg.educationLevel?.[0];
  const sen = tg.seniority?.[0];
  const ind = tg.industry?.[0];
  const jobCat = tg.jobCategory?.[0];

  const orderedProducts = campaign.orderedProducts || [];
  const orderedProductsSpecs = campaign.orderedProductsSpecs || [];
  const selectedCampaigns = orderedProducts
    .map((productId: string | number) => {
      const spec = orderedProductsSpecs.find(
        (s: { productId?: string | number }) => s.productId === productId
      );
      return {
        productId,
        id: productId,
        utm: spec?.utm,
      };
    })
    .filter((item: { productId?: string | number }) => item.productId);

  return {
    _isEditFlow: true,
    _prefillLoaded: false,
    vonqCampaignRecordId: campaign.id,
    jobSpecificationId: campaign.jobSpecificationId ?? null,
    jobVacancyTextId: campaign.jobVacancyTextId ?? null,
    vacancyTextVariant: (campaign.vacancyTextVariant as VacancyTextVariant) || null,
    Form1Data: {
      jobTitleSearchTerm: snapshot.jobTitle?.description || pd.title || "",
      locationSearchTerm: snapshot.location?.description || wl.city || "",
      jobTitle: toFormPair(snapshot.jobTitle),
      jobFunction: toFormPair(snapshot.jobFunction) || targetGroupPair(jobCat),
      location: toFormPair(snapshot.location),
      industries: toFormPair(snapshot.industry) || targetGroupPair(ind),
    },
    Form3Data: {
      education: targetGroupPair(edu) || toFormPair(snapshot.educationLevel),
      seniority: targetGroupPair(sen) || toFormPair(snapshot.seniority),
      jobFunction: targetGroupPair(jobCat) || toFormPair(snapshot.jobFunction),
      industry: targetGroupPair(ind) || toFormPair(snapshot.industry),
    },
    Form4Data: {
      recruiterFullName: recruiter.name || "",
      recruiterEmail: recruiter.emailAddress || "",
      contactFullName: contact.name || recruiter.name || "",
      contactEmail: contact.emailAddress || recruiter.emailAddress || "",
      contactPhone: contact.phoneNumber || "",
    },
    Form5Data: {
      campaignData: {
        campaignName: campaign.campaignName || "",
        jobTitle: pd.title || "",
        jobDescription: pd.description || "",
        yearsOfExperience: pd.yearsOfExperience ?? 0,
        employmentType: pd.employmentType || "",
        fromWorkingHours:
          weekly.from != null && weekly.from !== "" ? String(weekly.from) : "",
        fromWorkingMinutes: "0",
        toWorkingHours:
          weekly.to != null && weekly.to !== "" ? String(weekly.to) : "",
        toWorkingMinutes: "0",
        salaryIndicationFrom:
          salaryRange.from != null ? String(salaryRange.from) : "",
        salaryIndicationTo: salaryRange.to != null ? String(salaryRange.to) : "",
        currency: salaryRange.currency || campaign.currency || "",
        period: salary.period || "",
      },
      workingLocation: {
        addressLine1: wl.addressLine1 || "",
        addressLine2: wl.addressLine2 || "",
        postCode: wl.postcode || "",
        city: wl.city || "",
        country: wl.country || "",
        allowsRemoteWork: wl.allowsRemoteWork === 1,
      },
    },
    selectedCampaigns,
    selectedCampaign: selectedCampaigns,
    Form2Data: { selectedCampaigns },
  };
}

export function enrichWizardWithJobPrefill<T extends Record<string, any>>(
  wizardData: T,
  prefill: Record<string, any>
): T {
  const cp = prefill.campaignPrefill || {};
  const variants =
    cp.vacancyText?.variants ||
    (prefill.vacancyText
      ? {
          professional: prefill.vacancyText.professional,
          neutral: prefill.vacancyText.neutral,
          engaging: prefill.vacancyText.engaging,
        }
      : null);

  return {
    ...wizardData,
    jobSpecificationId:
      wizardData.jobSpecificationId ||
      prefill.campaignPrefill?.jobSpecificationId ||
      null,
    jobVacancyTextId:
      wizardData.jobVacancyTextId ||
      cp.vacancyText?.jobVacancyTextId ||
      prefill.vacancyText?.id ||
      null,
    vacancyTextVariants: variants || wizardData.vacancyTextVariants || null,
  };
}
